import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Badge, ListRow, tripHueColor } from "@pack-it-up/ui-web";
import { byOrder, type CalendarDate, type CategoryId, type FractionalIndex, type ParticipantId, type TemplateId, type TodoId, type TripColor, type UserId } from "@pack-it-up/shared";
import { createInlineEdit, type Neighbors, type ShortcutRegistry } from "@pack-it-up/viewmodel";
import { allTags, formatOffset, isCategoryCollapsed, isItemComplete, participantsOf } from "@pack-it-up/core";
import type { Assignment, Category, ItemDisplay, ParticipantContext, RelativeOffset, TemplateDisplay, TodoItem } from "@pack-it-up/core";
import { DndContext, DragOverlay, closestCenter, type DragStartEvent } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useStoreValue } from "@pack-it-up/viewmodel/bindings";
import { peopleOf } from "./people.js";
import { Sortable } from "../../features/dnd/Sortable.js";
import { useDndSensors } from "../../features/dnd/sensors.js";
import { makeCategoryDragEnd, makeRowDragEnd } from "../../features/dnd/dropHandlers.js";
import { DragDisabledHint } from "../../features/dnd/DragDisabledHint.js";
import { DragPreview } from "../../features/dnd/DragPreview.js";
import { AddRow } from "./AddRow.js";
import { AssignmentChips } from "./AssignmentChips.js";
import { CategorySection } from "./CategorySection.js";
import { CompletionControl } from "./CompletionControl.js";
import { ExpandedCompletionControl } from "./ExpandedCompletionControl.js";
import { ExpandedParticipantPills } from "./ExpandedParticipantPills.js";
import type { ItemComposerDraft } from "./ItemComposer.js";
import { TodoEditForm, type TodoPatchDraft } from "./RowEditForm.js";
import { RowName } from "./RowName.js";
import { RowTags } from "./RowTags.js";
import { DragHandle } from "./DragHandle.js";
import { SyncStatusBadge } from "./SyncStatusBadge.js";
import { SyncCategoryTick, SyncRowTick, type SyncListMode } from "./syncMode.js";
import type { ListActions } from "./ListActions.js";

// What a to-do row shows for "when": a trip's pinned calendar date, or a template's
// offset from the trip start (issue #188).
const DueBadge = ({ todo, dueMode }: { todo: TodoItem; dueMode: "date" | "relative" }): ReactNode => {
  if (dueMode === "relative") {
    return todo.relativeDueOffset !== undefined ? <Badge>{formatOffset(todo.relativeDueOffset)}</Badge> : null;
  }
  return todo.dueDate !== undefined ? <Badge>due {todo.dueDate}</Badge> : null;
};

export const TodoList = ({
  categories,
  todos,
  list,
  actor,
  showCompletion = true,
  dueMode = "date",
  onSetRelativeDue,
  canEdit,
  sort,
  registry,
  syncMode,
  itemDisplay = "condensed",
  onToggle,
  onToggleAllParticipants,
  onCheckAll,
  onUncheckAll,
  onToggleCollapse,
  onAddTodo,
  onAddCategory,
  onUpdateCategory,
  onDeleteCategory,
  onUpdateTodo,
  onPinDueDate,
  onSetAssignment,
  onDeleteTodo,
  onMoveTodo,
  onReorderCategory,
  tagPalette,
  templateDisplay = "tag",
  resolveTemplateColor,
}: {
  categories: Category[];
  todos: TodoItem[];
  // Trip or template (issue #188) — see PackingList.
  list: ParticipantContext;
  // Absent for a link-grant viewer (Doc 08 §4a) — see PackingList.
  actor?: UserId;
  // Issue #188: a template's to-dos carry no completion. Staging is a packing-item
  // concept, and there is nothing to tick off until the template has been imported, so
  // the checkbox and the per-person chips' toggling are both suppressed.
  showCompletion?: boolean;
  // A template's to-dos are due relative to the trip's start date, not on a calendar
  // date (Doc 07 §6) — the composer swaps its date field for the offset control, and the
  // resulting offset is applied through `onSetRelativeDue`.
  dueMode?: "date" | "relative";
  onSetRelativeDue?: (todoId: TodoId, offset: RelativeOffset | null) => void;
  sort: "manual" | "alphabetical" | "dueDate";
  registry: ShortcutRegistry;
  // T28 sync mode — see PackingList.
  syncMode?: SyncListMode;
  // Issue #203 — see PackingList. A template's to-dos (showCompletion: false) have no
  // "me" for this to apply to; always condensed there regardless of the prop value.
  itemDisplay?: ItemDisplay;
  // Tags in use on this trip/template (T29) — offered in the add/edit composers.
  tagPalette?: string[];
  // Trip setting: a row's source-template auto-tag renders as a tag chip ("tag",
  // default) or as a color bar on the row's right edge ("highlight"). Omit for "tag".
  templateDisplay?: TemplateDisplay;
  // Resolves a template's color for the highlight bar — required when templateDisplay
  // is "highlight"; unused otherwise. See PackingList.
  resolveTemplateColor?: (templateId: TemplateId) => TripColor;
  onToggle: (todoId: string, target: ParticipantId, targetIsUser: boolean) => void;
  // Required when itemDisplay is "expanded"; unused in condensed mode.
  onToggleAllParticipants?: (todoId: string) => void;
  // "More" menu bulk actions (Check all / Uncheck all). Omit either to hide that item.
  onCheckAll?: (categoryId: CategoryId) => void;
  onUncheckAll?: (categoryId: CategoryId) => void;
  onAddTodo: (categoryId: CategoryId, draft: ItemComposerDraft) => void;
  onUpdateTodo: (todoId: TodoId, patch: TodoPatchDraft) => void;
  onPinDueDate: (todoId: TodoId, date: CalendarDate) => void;
  onSetAssignment: (todoId: TodoId, assignment: Assignment) => void;
  // Trash icon on the edit form — see PackingList's onDeleteItem.
  onDeleteTodo?: (todoId: TodoId) => void;
  onMoveTodo: (rowId: string, toCategoryId: CategoryId, neighbors: Neighbors) => void;
  onReorderCategory: (categoryId: CategoryId, order: FractionalIndex) => void;
} & ListActions): ReactNode => {
  const edit = useMemo(() => createInlineEdit(), []);
  const editingId = useStoreValue(edit, (s) => (s.mode === "editing" ? s.targetId : null));
  const close = (): void => void edit.getState().cancel();
  const participants = participantsOf(list);
  const people = useMemo(() => peopleOf(list), [list]);
  const isDone = (t: TodoItem): boolean => showCompletion && isItemComplete(t, participants);
  const isManual = sort === "manual";
  const sortedCategories = [...categories].sort(byOrder);
  // A template's to-dos (showCompletion: false) or a sync-mode row has no "me" for this
  // self-scoped preference to apply to — always condensed there.
  const expanded = itemDisplay === "expanded" && showCompletion && syncMode === undefined;

  // Esc cancels the open row edit (Doc 06 §8) — see PackingList for rationale.
  useEffect(() => registry.register("editor", { escape: () => edit.getState().cancel() }), [registry, edit]);

  const catSensors = useDndSensors();
  const todoSensors = useDndSensors();
  const handleCatDragEnd = makeCategoryDragEnd(sortedCategories, onReorderCategory);
  const handleTodoDragEnd = makeRowDragEnd(todos, sortedCategories, onMoveTodo);
  // See PackingList for why this needs a DragOverlay ghost rather than relying on the
  // dragged row's own (origin-category-bound) node.
  const [draggingTodo, setDraggingTodo] = useState<TodoItem | null>(null);
  const handleTodoDragStart = (event: DragStartEvent): void =>
    setDraggingTodo(todos.find((t) => t.id === event.active.id) ?? null);

  return (
    <>
      {!isManual && canEdit && <DragDisabledHint />}
      <DndContext sensors={catSensors} collisionDetection={closestCenter} onDragEnd={handleCatDragEnd}>
        <SortableContext items={sortedCategories.map((c) => c.id)} strategy={verticalListSortingStrategy} disabled={!isManual}>
          <DndContext
            sensors={todoSensors}
            collisionDetection={closestCenter}
            onDragStart={handleTodoDragStart}
            onDragEnd={(event) => {
              handleTodoDragEnd(event);
              setDraggingTodo(null);
            }}
            onDragCancel={() => setDraggingTodo(null)}
          >
            {sortedCategories.map((c, catIdx) => {
              // Same as PackingList: `todos` already carry the view's order.
              const catTodos = todos.filter((t) => t.categoryId === c.id);
              const done = catTodos.filter(isDone).length;
              const countLabel = !showCompletion
                ? String(catTodos.length)
                : catTodos.length === 0
                  ? "0 / 0"
                  : `${done} / ${catTodos.length}${done === catTodos.length ? " ✓" : ""}`;
              return (
                <Sortable key={c.id} id={c.id} disabled={!isManual || !canEdit}>
                  {(catHandleProps) => (
                    <SortableContext
                      id={c.id}
                      items={catTodos.map((t) => t.id)}
                      strategy={verticalListSortingStrategy}
                      disabled={!isManual}
                    >
                      <CategorySection
                        name={c.name}
                        count={countLabel}
                        collapsed={isCategoryCollapsed(c, actor)}
                        canEdit={canEdit}
                        onToggleCollapse={() => onToggleCollapse(c.id)}
                        onAdd={(name) => onAddTodo(c.id, { name, quantity: 1, assignment: { kind: "everyone" }, notes: "", dueDate: "", tags: [] })}
                        people={people}
                        {...(tagPalette !== undefined ? { tagPalette } : {})}
                        onAddItem={(draft) => onAddTodo(c.id, draft)}
                        itemVariant="todo"
                        onRename={(name) => onUpdateCategory(c.id, { name })}
                        onDelete={() => onDeleteCategory(c)}
                        {...(canEdit && showCompletion && catTodos.length > 0 && onCheckAll !== undefined ? { onCheckAll: () => onCheckAll(c.id) } : {})}
                        {...(canEdit && showCompletion && catTodos.length > 0 && onUncheckAll !== undefined ? { onUncheckAll: () => onUncheckAll(c.id) } : {})}
                        {...(isManual && canEdit ? { dragHandleProps: catHandleProps } : {})}
                        {...(catIdx === 0 ? { addShortcut: registry } : {})}
                        {...(syncMode !== undefined
                          ? { leading: <SyncCategoryTick mode={syncMode} categoryId={c.id} name={c.name} /> }
                          : {})}
                      >
                        {catTodos.map((t) => {
                          const isEditing = editingId === t.id;
                          const syncEntry = syncMode?.entryFor("todos", t.id);
                          // See PackingList: "Show template tags as: highlight" swaps the
                          // source-template auto-tag for a color bar instead of a tag chip.
                          const highlightSource = templateDisplay === "highlight" ? t.provenance?.templateSources[0] : undefined;
                          const accentColor =
                            highlightSource !== undefined && resolveTemplateColor !== undefined
                              ? tripHueColor[resolveTemplateColor(highlightSource.templateId)]
                              : undefined;
                          const displayTags = templateDisplay === "highlight" ? (t.tags ?? []) : allTags(t);
                          return (
                            <Sortable key={t.id} id={t.id} disabled={!isManual || !canEdit}>
                              {(handleProps) => (
                                <ListRow
                                  aria-label={t.name}
                                  completed={isDone(t)}
                                  editing={isEditing}
                                  {...(syncMode === undefined && accentColor !== undefined ? { accentColor } : {})}
                                  {...(canEdit && !isEditing ? { onClick: () => edit.getState().beginEdit(t.id, t.name) } : {})}
                                  leading={
                                    syncMode !== undefined ? (
                                      syncEntry !== undefined ? <SyncRowTick mode={syncMode} entry={syncEntry} name={t.name} /> : undefined
                                    ) : isEditing ? undefined : (
                                      <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }} onClick={(e) => e.stopPropagation()}>
                                        {isManual && canEdit ? (
                                          <DragHandle listeners={handleProps.listeners} attributes={handleProps.attributes} />
                                        ) : null}
                                        {showCompletion && expanded && actor !== undefined && t.assignment.kind !== "shared" ? (
                                          <ExpandedCompletionControl
                                            item={t}
                                            list={list}
                                            actor={actor}
                                            canEdit={canEdit}
                                            imperative="Complete"
                                            onAdvanceAll={() => onToggleAllParticipants?.(t.id)}
                                          />
                                        ) : showCompletion ? (
                                          <CompletionControl
                                            item={t}
                                            list={list}
                                            canEdit={canEdit}
                                            imperative="Complete"
                                            pastTense="done"
                                            {...(actor !== undefined ? { actor } : {})}
                                            onToggle={(target, isUser) => onToggle(t.id, target, isUser)}
                                          />
                                        ) : null}
                                      </span>
                                    )
                                  }
                                  trailing={
                                    syncMode !== undefined ? (
                                      <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                                        <DueBadge todo={t} dueMode={dueMode} />
                                        {syncEntry !== undefined && <SyncStatusBadge entry={syncEntry} />}
                                      </span>
                                    ) : isEditing ? undefined : (
                                      <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                                        <DueBadge todo={t} dueMode={dueMode} />
                                        <RowTags tags={displayTags} />
                                        {(!expanded || t.assignment.kind === "shared") && (
                                          <AssignmentChips
                                            item={t}
                                            list={list}
                                            pastTense="done"
                                            canEdit={canEdit && showCompletion}
                                            {...(showCompletion ? { onToggle: (target: ParticipantId, isUser: boolean) => onToggle(t.id, target, isUser) } : {})}
                                          />
                                        )}
                                      </span>
                                    )
                                  }
                                >
                                  {isEditing ? (
                                    <TodoEditForm
                                      todo={t}
                                      people={people}
                                      canEdit={canEdit}
                                      {...(tagPalette !== undefined ? { tagPalette } : {})}
                                      onCommit={(patch) => {
                                        if (Object.keys(patch).length > 0) onUpdateTodo(t.id, patch);
                                        close();
                                      }}
                                      onCancel={close}
                                      dueMode={dueMode}
                                      {...(onSetRelativeDue !== undefined
                                        ? { onSetRelativeDue: (offset: RelativeOffset | null) => onSetRelativeDue(t.id, offset) }
                                        : {})}
                                      onPinDueDate={(date) => onPinDueDate(t.id, date)}
                                      onSetAssignment={(asg) => onSetAssignment(t.id, asg)}
                                      {...(onDeleteTodo !== undefined
                                        ? {
                                            onDelete: () => {
                                              onDeleteTodo(t.id);
                                              close();
                                            },
                                          }
                                        : {})}
                                    />
                                  ) : (
                                    <>
                                      <RowName name={t.name} description={t.description} />
                                      {expanded && actor !== undefined && (
                                        <ExpandedParticipantPills
                                          item={t}
                                          list={list}
                                          actor={actor}
                                          pastTense="done"
                                          canEdit={canEdit}
                                          onToggle={(target, isUser) => onToggle(t.id, target, isUser)}
                                        />
                                      )}
                                    </>
                                  )}
                                </ListRow>
                              )}
                            </Sortable>
                          );
                        })}
                      </CategorySection>
                    </SortableContext>
                  )}
                </Sortable>
              );
            })}
            {/* See PackingList: dropAnimation off avoids the ghost easing to a stale
                (pre-reorder) rect and then blinking into its real new position. */}
            <DragOverlay dropAnimation={null}>{draggingTodo !== null && <DragPreview name={draggingTodo.name} />}</DragOverlay>
          </DndContext>
        </SortableContext>
      </DndContext>
      {canEdit && <AddRow label="Add a new category" onAdd={onAddCategory} variant="dashed" />}
    </>
  );
};
